import axios from "axios";
import $URL from "@/tool/url";
import API from "@/tool/api";
import {
    clearAccessToken,
    getAccessToken,
    setAccessToken,
} from "@/tool/authStorage";

const api = axios.create({
    baseURL: $URL.baseUrl,
    withCredentials: true,
});

const AUTH_ENDPOINTS = [
    API.auth.login,
    API.auth.loginSmsCode,
    API.auth.register,
    API.auth.smsCode,
    API.auth.resetPasswordSmsCode,
    API.auth.resetPassword,
    API.auth.refresh,
    API.auth.logout,
];

const REFRESH_TIMEOUT = 10000;

let refreshPromise = null;
let authFailureHandler = () => {};
let authFailureNotified = false;

const normalizeUrl = (url) => {
    if (typeof url !== "string") {
        return url;
    }
    return url.replace(/^\/+/, "");
};

const resolveUrl = (url) => {
    if (typeof url !== "string") {
        return url;
    }
    const basePath = normalizeUrl($URL.baseUrl);
    const normalizedUrl = normalizeUrl(url);
    if (basePath && normalizedUrl.indexOf(basePath) === 0) {
        return normalizedUrl.slice(basePath.length);
    }
    return url.indexOf("/") === 0 ? url : normalizeUrl(url);
};

const getRequestPath = (config) => normalizeUrl(config && config.url) || "";

const isAuthEndpoint = (config) => {
    const path = getRequestPath(config).split("?")[0];
    return AUTH_ENDPOINTS.indexOf(path) !== -1;
};

const createRefreshError = (message, response) => {
    const error = new Error(message);
    error.response = response;
    return error;
};

const readPayloadData = (response) => {
    const payload = response && response.data ? response.data : {};
    return payload.data !== undefined ? payload.data : payload.result;
};

const refreshAccessTokenInternal = () => api.request({
    method: "POST",
    url: API.auth.refresh,
    skipAuth: true,
    skipRefresh: true,
    timeout: REFRESH_TIMEOUT,
}).then((response) => {
    const payload = response.data || {};
    const data = readPayloadData(response) || {};
    const token = data.accessToken;

    if (payload.code !== 200 || payload.success === false || !token) {
        throw createRefreshError(payload.message || "Failed to refresh access token", response);
    }

    setAccessToken(token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    return token;
});

const getRefreshPromise = () => {
    if (!refreshPromise) {
        authFailureNotified = false;
        refreshPromise = refreshAccessTokenInternal()
            .catch((error) => {
                clearAccessToken();
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

const notifyAuthFailure = (error, shouldRedirect = true) => {
    if (!authFailureNotified) {
        authFailureNotified = true;
        clearAccessToken();
        if (shouldRedirect) {
            authFailureHandler(error);
        }
    }
};

const hasAuthContext = (config) => {
    const headers = config && config.headers ? config.headers : {};
    return !!getAccessToken() || !!headers.Authorization || !!headers.authorization;
};

const clearAuthorizationHeaders = (headers) => {
    Object.keys(headers || {}).forEach((name) => {
        if (name.toLowerCase() === "authorization") {
            delete headers[name];
        }
    });
};

const shouldRefresh = (config) => {
    return !!config &&
        config.skipRefresh !== true &&
        config.skipAuth !== true &&
        config._authRetry !== true &&
        hasAuthContext(config) &&
        !isAuthEndpoint(config);
};

const retryAfterRefresh = (error) => {
    const config = error && error.config;
    const authContext = hasAuthContext(config);
    if (!shouldRefresh(config)) {
        return Promise.reject(error);
    }

    const retryConfig = {
        ...config,
        _authRetry: true,
        headers: {
            ...(config.headers || {}),
        },
    };
    clearAuthorizationHeaders(retryConfig.headers);
    delete retryConfig.skipRefresh;

    return getRefreshPromise().then(
        () => {
            return api.request(retryConfig).catch((retryError) => {
                const retryPayload = retryError.response && retryError.response.data;
                const authError = (retryError.response && retryError.response.status === 401) ||
                    (retryPayload && retryPayload.code === 401);
                if (authError) {
                    notifyAuthFailure(retryError, authContext);
                }
                return Promise.reject(retryError);
            });
        },
        (refreshError) => {
            notifyAuthFailure(refreshError, authContext);
            return Promise.reject(error);
        },
    );
};

api.interceptors.request.use((config) => {
    config.url = resolveUrl(config.url);
    config.headers = config.headers || {};

    if (config.skipAuth !== true) {
        const token = getAccessToken();
        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }

    if (typeof config.data === "string" && !config.headers["Content-Type"]) {
        config.headers["Content-Type"] = "application/x-www-form-urlencoded";
    }

    return config;
});

api.interceptors.response.use((response) => {
    if (response && response.data && response.data.code === 401) {
        const error = createRefreshError(response.data.message || "Login credential expired", response);
        error.config = response.config;
        return retryAfterRefresh(error);
    }
    return response;
}, (error) => {
    if (error && error.response && error.response.status === 401) {
        return retryAfterRefresh(error);
    }
    return Promise.reject(error);
});

export const configureRequest = ({ onAuthFailure } = {}) => {
    authFailureHandler = typeof onAuthFailure === "function" ? onAuthFailure : () => {};
};

export const refreshAccessToken = () => getRefreshPromise();

export const restoreSession = () => {
    const token = getAccessToken();
    if (token) {
        return Promise.resolve(token);
    }
    return refreshAccessToken().catch(() => null);
};

const request = (configOrUrl) => {
    if (typeof configOrUrl === "string") {
        return api.request({ url: configOrUrl });
    }
    return api.request(configOrUrl || {});
};

request.get = (url, config = {}) => request({
    ...config,
    method: "GET",
    url,
});

request.post = (url, data, config = {}) => request({
    ...config,
    method: "POST",
    url,
    data,
});

request.put = (url, data, config = {}) => request({
    ...config,
    method: "PUT",
    url,
    data,
});

request.delete = (url, config = {}) => request({
    ...config,
    method: "DELETE",
    url,
});

// Only the server's narrowly scoped COS PUT capability may bypass business authentication.
request.putSignedObject = async (ticket, file, onProgress = () => {}) => {
    const url = new URL(ticket.uploadUrl);
    if (url.protocol !== 'https:' || !/^[a-z0-9-]+\.cos\.[a-z0-9-]+\.myqcloud\.com$/i.test(url.hostname) || url.username || url.password) {
        throw new Error('上传地址不是受控COS能力');
    }
    const headers = { ...(ticket.requiredHeaders || {}) };
    if (Object.keys(headers).some(name => !/^(content-type|x-cos-forbid-overwrite|x-cos-meta-[a-z0-9-]+)$/i.test(name)) ||
        headers['x-cos-forbid-overwrite'] !== 'true' || !['image/jpeg', 'image/tiff'].includes(headers['Content-Type'])) {
        throw new Error('上传授权缺少不可覆盖保护或包含不受信任请求头');
    }
    return axios.request({ method:'PUT', url:ticket.uploadUrl, data:file, headers,
        withCredentials:false, timeout:120000, transformRequest:[data => data],
        onUploadProgress: event => onProgress(Math.min(event.loaded, file.size)),
    });
};

// Fetch streams allow a Bearer header without placing an access token in the SSE URL.
request.streamEvents = async (url, { data, onEvent, onDisconnect = () => {} } = {}) => {
    if (!/^photography\/creator\/service-orders\/[^/]+\/retouch\/upload-events$/.test(url)) {
        throw new Error('结果通道地址无效');
    }
    const controller = new AbortController();
    const target = new URL(url, new URL($URL.baseUrl, window.location.href));
    const open = () => fetch(target.toString(), {method:'POST',body:JSON.stringify(data),credentials:'include',cache:'no-store',signal:controller.signal,
        headers:{Accept:'text/event-stream','Content-Type':'application/json',Authorization:`Bearer ${getAccessToken() || ''}`},
    });
    let response=await open();
    if(response.status===401) {await getRefreshPromise();response=await open();}
    if(!response.ok || !response.body || !/^text\/event-stream/i.test(response.headers.get('Content-Type') || '')) {
        controller.abort();const error=new Error('无法连接结果通道，请在当前页重试');error.response={status:response.status};throw error;
    }
    const reader=response.body.getReader();const decoder=new TextDecoder();let pending='';let stopped=false;
    const close=()=>{stopped=true;controller.abort();reader.cancel().catch(()=>{});};
    (async()=>{
        try {
            while(!stopped) {
                const chunk=await reader.read();if(chunk.done)break;
                pending=(pending+decoder.decode(chunk.value,{stream:true})).replace(/\r\n/g,'\n');
                if(pending.length>1024*1024)throw new Error('结果消息过大');
                let boundary;
                while((boundary=pending.indexOf('\n\n'))>=0) {
                    const block=pending.slice(0,boundary);pending=pending.slice(boundary+2);
                    const content=block.split('\n').filter(line=>line.startsWith('data:')).map(line=>line.slice(5).trimStart()).join('\n');
                    if(content) onEvent(JSON.parse(content));
                }
            }
            if(!stopped)onDisconnect();
        }catch(error){if(!stopped)onDisconnect(error);}
        finally {reader.releaseLock();}
    })();
    return close;
};

export default request;

// COS能力不携带站点Cookie或Authorization，也不参加登录刷新。
request.readAuthorizedBlob = async function(url) {
    const response = await fetch(url, { credentials: 'omit', cache: 'no-store' });
    if (!response.ok) throw Error(response.status === 403 ? '下载签名已过期，请重新领取授权' : 'JPG下载失败，请重试');
    return response.blob();
}

// TIFF/ZIP使用浏览器流式下载，避免把数百张原件的归档放入页面Blob内存。
// URL必须来自本次本人授权；GET签名不能用HEAD预检，浏览器完成结果不能由页面伪报。
request.startAuthorizedDownload = async function(access) {
    if (!access || !/^https:\/\//.test(access.url || '') || !access.downloadFilename) throw Error('下载授权无效，请重新领取');
    const link = document.createElement('a');
    link.href = access.url; link.download = access.downloadFilename; link.rel = 'noopener';
    document.body.appendChild(link); link.click(); link.remove();
};
